import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import ShareStrip from "@/components/common/ShareStrip";
import {
  X,
  Send,
  Loader2,
  CheckCircle2,
  User,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactFormModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_address: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const openedAtRef = useRef(0);

  useEffect(() => {
    if (open) openedAtRef.current = Date.now();
  }, [open]);

  const reset = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      business_address: "",
      message: "",
      website: "",
    });
    setSuccess(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!name || !email || !phone) {
      toast.error("Full name, email and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name,
        email,
        phone,
        business_address: form.business_address.trim(),
        project: "Contact form",
        message: form.message.trim(),
        website: form.website,
        form_ms: openedAtRef.current
          ? Date.now() - openedAtRef.current
          : undefined,
      });
      setSuccess(true);
      setTimeout(() => {
        reset();
        onClose?.();
      }, 1800);
    } catch (err) {
      toast.error("Couldn't send. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const inputCls =
    "w-full bg-white/[0.03] border border-white/10 focus:border-[#00f0ff]/50 focus:bg-white/[0.05] focus:outline-none rounded-xl pl-11 pr-4 py-3.5 text-[14px] text-white placeholder:text-white/30 transition-all";

  return (
    <AnimatePresence>
      <motion.div
        key="contact-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="contact-form-modal"
        className="fixed inset-0 z-[410] overflow-y-auto bg-black/85 backdrop-blur-md"
      >
        <div className="min-h-full flex items-start justify-center px-3 py-6 sm:py-10">
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg my-4 rounded-3xl bg-[#0a0612] border border-white/10 shadow-[0_30px_120px_rgba(255,0,85,0.18)] overflow-hidden"
          >
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#ff0055]/20 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-[#00f0ff]/15 blur-3xl pointer-events-none" />
            <div className="relative px-6 md:px-8 pt-7 pb-6">
              <ShareStrip campaign="contact_form" title="SB Media Studio" text="Reach out — podcast, video, UGC, and headshots in Lakeland, FL." className="mr-1" />
              <button
                type="button"
                onClick={onClose}
                data-testid="contact-form-close"
                aria-label="Close"
                className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center hover:bg-white/[0.12]"
              >
                <X size={14} />
              </button>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#ff0055] mb-2">
                [ Contact us ]
              </div>
              <h3 className="font-display font-black text-2xl md:text-[28px] tracking-tight">
                Let&apos;s{" "}
                <span className="italic font-light text-[#a395c3]">start</span>{" "}
                the conversation.
              </h3>
              <p className="text-[#a395c3] text-sm mt-1.5">
                Drop your details — we&apos;ll reach out within 24 hours.
              </p>
            </div>

            <div className="relative px-6 md:px-8 pb-8">
              {success ? (
                <div
                  data-testid="contact-form-success"
                  className="rounded-2xl border border-[#00f0ff]/40 bg-[#00f0ff]/[0.06] p-6 flex items-start gap-3"
                >
                  <CheckCircle2
                    size={22}
                    className="text-[#00f0ff] flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <div className="font-display font-bold text-white text-base">
                      Sent — we&apos;ve got you.
                    </div>
                    <div className="text-sm text-[#a395c3] mt-1 leading-relaxed">
                      Your details are with OJ and Yasmin. Expect a reply
                      within 24 hours (usually sooner).
                    </div>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="space-y-3"
                  data-testid="contact-us-form"
                >
                  {/* Honeypot — hidden from users, bots often fill anything named "website" */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, website: e.target.value }))
                    }
                    aria-hidden="true"
                    className="absolute left-[-9999px] w-px h-px opacity-0 pointer-events-none"
                  />
                  <div className="relative">
                    <User
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a395c3] pointer-events-none"
                    />
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                      placeholder="Full name *"
                      className={inputCls}
                      data-testid="contact-us-name"
                    />
                  </div>
                  <div className="relative">
                    <Mail
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a395c3] pointer-events-none"
                    />
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, email: e.target.value }))
                      }
                      placeholder="Email *"
                      className={inputCls}
                      data-testid="contact-us-email"
                    />
                  </div>
                  <div className="relative">
                    <Phone
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a395c3] pointer-events-none"
                    />
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, phone: e.target.value }))
                      }
                      placeholder="Phone *"
                      className={inputCls}
                      data-testid="contact-us-phone"
                    />
                  </div>
                  <div className="relative">
                    <MapPin
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a395c3] pointer-events-none"
                    />
                    <input
                      type="text"
                      value={form.business_address}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          business_address: e.target.value,
                        }))
                      }
                      placeholder="Business address (optional)"
                      className={inputCls}
                      data-testid="contact-us-address"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-3 gap-3">
                    <div className="text-[11px] text-[#a395c3] flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff] animate-pulse" />
                      Replies within 24 hours
                    </div>
                    
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-us-submit"
                      className="group inline-flex items-center gap-3 pl-5 pr-2 py-2.5 rounded-full bg-[#ff0055] text-white font-semibold text-sm hover:bg-[#00f0ff] hover:text-[#05050a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          Sending
                          <Loader2 size={14} className="animate-spin" />
                        </>
                      ) : (
                        <>
                          Send
                          <span className="w-8 h-8 rounded-full bg-black/25 group-hover:bg-[#05050a] flex items-center justify-center group-hover:rotate-[-45deg] transition-all">
                            <Send size={12} className="text-white group-hover:text-[#00f0ff] transition-colors" />
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ContactFormModal;
